import React, { useState, useRef } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Phone, PhoneCall, Bot, CheckCircle, XCircle, PhoneOff, Volume2,
  Clock, ChevronDown, FileText, Play, Pause, Mic, Calendar, Mail,
  Sparkles, AlertCircle, Voicemail
} from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import api from '../lib/api';

const STATUS_CONFIG = {
  initiated: { label: 'Initiated', icon: Phone, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', dot: 'bg-blue-500' },
  ringing: { label: 'Ringing', icon: PhoneCall, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  'in-progress': { label: 'In Progress', icon: Volume2, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400', dot: 'bg-indigo-500' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500' },
  'no-answer': { label: 'No Answer', icon: PhoneOff, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800/40 dark:text-gray-400', dot: 'bg-gray-500' },
  busy: { label: 'Busy', icon: PhoneOff, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  canceled: { label: 'Canceled', icon: XCircle, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800/40 dark:text-gray-400', dot: 'bg-gray-500' },
  unknown: { label: 'Unknown', icon: PhoneOff, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800/40 dark:text-gray-400', dot: 'bg-gray-400' },
};

const PROVIDER_CONFIG = {
  voicelink: { label: 'VoiceLink', color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800' },
  twilio: { label: 'Twilio', color: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400 dark:border-indigo-800' },
  exotel: { label: 'Exotel', color: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/40 dark:text-sky-400 dark:border-sky-800' },
};


const CALL_TYPE_LABELS = {
  ai_outbound: 'AI Outbound',
  discovery: 'Discovery',
  demo_scheduling: 'Demo Scheduling',
  followup: 'Follow-up',
  closing: 'Closing',
  re_engagement: 'Re-engagement',
  outgoing: 'Outgoing',
  incoming: 'Incoming',
  missed: 'Missed',
};

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

export function RecordingPlayer({ url, recordingSid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  // Build proxy URL
  const getProxySid = () => {
    let sid = recordingSid;
    if (!sid && url) {
      const match = url.match(/Recordings\/(RE[a-f0-9]+)/);
      if (match) sid = match[1];
    }
    return sid;
  };

  const loadAudio = async () => {
    if (blobUrl) return blobUrl;

    // AI brain calls (ElevenLabs ConvAI) expose a direct API recording path
    // (e.g. /api/leads/{id}/ai-call/{call_id}/recording). Twilio calls only
    // have a recording SID, fetched via the /api/voice/recordings proxy.
    let fetchUrl;
    if (url && url.startsWith('/api/')) {
      fetchUrl = url;
    } else {
      const sid = getProxySid();
      if (!sid) { setError(true); return null; }
      fetchUrl = `/api/voice/recordings/${sid}`;
    }

    setLoading(true);
    setNotReady(false);
    try {
      const resp = await api.get(fetchUrl, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(resp.data);
      setBlobUrl(objUrl);
      return objUrl;
    } catch (e) {
      // 409 = recording generated but audio not finalized yet (ElevenLabs still
      // rendering). Show a "processing" hint the user can retry, not a hard error.
      if (e?.response?.status === 409) {
        setNotReady(true);
      } else {
        setError(true);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    // Lazy-load audio on first play
    if (!blobUrl) {
      const objUrl = await loadAudio();
      if (!objUrl) return;
      audioRef.current.src = objUrl;
      audioRef.current.load();
      // Wait for loadedmetadata before playing
      await new Promise(resolve => {
        audioRef.current.onloadedmetadata = () => {
          setDuration(audioRef.current.duration);
          resolve();
        };
      });
    }
    audioRef.current.play().catch(() => setError(true));
    setPlaying(true);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  const handleEnded = () => setPlaying(false);

  const handleSeek = (e) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const fmtTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-3 py-2" data-testid="call-recording-player">
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={() => setError(true)}
      />
      {error ? (
        <span className="text-xs text-muted-foreground">Recording unavailable</span>
      ) : notReady ? (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          onClick={togglePlay}
          data-testid="recording-retry-btn"
        >
          Recording is still processing — tap to retry
        </button>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0 rounded-full"
            onClick={togglePlay}
            disabled={loading}
            data-testid="recording-play-btn"
          >
            {loading ? (
              <span className="h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
            ) : playing ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          <div className="flex-1 min-w-0">
            <div
              className="h-1.5 bg-border rounded-full cursor-pointer relative overflow-hidden"
              onClick={handleSeek}
              data-testid="recording-progress-bar"
            >
              <div
                className="absolute left-0 top-0 h-full bg-primary rounded-full"
                style={{ width: `${progress}%`, transition: 'width 0.1s linear' }}
              />
            </div>
          </div>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
            {fmtTime(audioRef.current?.currentTime || 0)} / {fmtTime(duration)}
          </span>
        </>
      )}
    </div>
  );
}

function CallLogCardImpl({ call, highlighted, getCreatorName }) {
  const [expanded, setExpanded] = useState(false);

  const isAI = call.is_ai_call || call.call_type === 'ai_outbound' || call.source === 'brain_call';
  const status = STATUS_CONFIG[call.status] || STATUS_CONFIG['unknown'];
  const StatusIcon = status.icon;
  const callDate = call.created_at || call.createdAt;
  // Both duration_seconds and the legacy Twilio `duration` field are in SECONDS.
  const durationStr = formatDuration(call.duration_seconds ?? call.duration);
  const typeLabel = CALL_TYPE_LABELS[call.call_type] || call.call_type || 'Call';
  const summary = call.summary || call.ai_summary || call.notes;
  const transcript = call.transcript || call.whisper_transcript;
  const recordingUrl = call.recording_url;
  const hasMeeting = call.scheduled_meeting;
  // Phase 1 (G1): failed LIVE calls now carry the REAL provider error.
  const failureReason = call.status === 'failed'
    ? (call.error || call.failure_reason || 'The call could not be connected. No further details were returned by the carrier.')
    : null;
  const failoverFrom = call.failover_from;
  const hasExpandable = summary || transcript || recordingUrl || failureReason;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div
        className={`border rounded-lg bg-card overflow-hidden transition-shadow duration-200 ${
          highlighted ? 'ring-2 ring-primary shadow-md' : 'border-border hover:shadow-sm'
        } ${isAI ? 'border-l-[3px] border-l-teal-500' : ''}`}
        data-testid={`call-record-${call.id}`}
      >
        {/* Header Row */}
        <CollapsibleTrigger asChild disabled={!hasExpandable}>
          <button
            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${hasExpandable ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'}`}
            data-testid={`call-expand-btn-${call.id}`}
          >
            {/* Status Dot */}
            <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${status.dot}`} />

            {/* Main Info */}
            <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">{typeLabel}</span>
              {isAI && (
                <Badge className="text-[10px] px-1.5 py-0 bg-teal-100 text-teal-700 border border-teal-200 dark:bg-teal-900/40 dark:text-teal-400 dark:border-teal-800" data-testid={`call-ai-badge-${call.id}`}>
                  <Bot className="h-3 w-3 mr-0.5" /> AI
                </Badge>
              )}
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${status.color}`} data-testid={`call-status-badge-${call.id}`}>
                <StatusIcon className="h-3 w-3 mr-0.5" />
                {status.label}
              </Badge>
              {call.provider && PROVIDER_CONFIG[call.provider] && (
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${PROVIDER_CONFIG[call.provider].color}`} data-testid={`call-provider-badge-${call.id}`} title={`Placed via ${PROVIDER_CONFIG[call.provider].label}`}>
                  {PROVIDER_CONFIG[call.provider].label}
                </Badge>
              )}
              {call.mode === 'SIMULATED' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400">
                  Simulated
                </Badge>
              )}
              {call.is_voicemail && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400" data-testid={`call-voicemail-badge-${call.id}`} title="Answering machine detected — LEO left a courtesy voicemail">
                  <Voicemail className="h-3 w-3 mr-0.5" /> Voicemail
                </Badge>
              )}
              {failoverFrom && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-400" data-testid={`call-failover-badge-${call.id}`} title={`Primary carrier ${failoverFrom} failed — automatically connected via ${call.provider}`}>
                  Failover
                </Badge>
              )}
              {failureReason && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 max-w-[220px]" data-testid={`call-fail-reason-badge-${call.id}`} title={failureReason}>
                  <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                  <span className="truncate">Why it failed</span>
                </Badge>
              )}
            </div>

            {/* Right Side: Duration + Date + Chevron */}
            <div className="flex items-center gap-3 shrink-0">
              {durationStr && (
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`call-duration-${call.id}`}>
                  <Clock className="h-3 w-3" /> {durationStr}
                </span>
              )}
              {call.conversation_turns > 0 && (
                <span className="text-xs text-muted-foreground" data-testid={`call-turns-${call.id}`}>
                  {call.conversation_turns} turns
                </span>
              )}
              <span className="text-xs text-muted-foreground tabular-nums">
                {callDate ? formatLocal(callDate, 'MMM dd, HH:mm') : ''}
              </span>
              {hasExpandable && (
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        {/* Expandable Content */}
        <CollapsibleContent>
          <div className="border-t border-border divide-y divide-border">
            {/* Failure reason (Phase 1 / G1 — real carrier error surfaced) */}
            {failureReason && (
              <div className="px-4 py-3 bg-red-50/50 dark:bg-red-950/10" data-testid={`call-failure-reason-${call.id}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                  <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide">Why this call failed</span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">{failureReason}</p>
                {failoverFrom && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Tried <span className="font-medium">{failoverFrom}</span> first, then automatically failed over to <span className="font-medium">{call.provider}</span>.
                  </p>
                )}
              </div>
            )}

            {/* AI Summary */}
            {summary && (
              <div className="px-4 py-3" data-testid={`call-summary-${call.id}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-teal-500" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Summary</span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">{summary}</p>
              </div>
            )}

            {/* Recording Player */}
            {recordingUrl && (
              <div className="px-4 py-3" data-testid={`call-recording-${call.id}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Mic className="h-3.5 w-3.5 text-teal-500" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recording</span>
                </div>
                <RecordingPlayer url={recordingUrl} recordingSid={call.recording_sid} />
              </div>
            )}

            {/* Transcript */}
            {transcript && (
              <div className="px-4 py-3" data-testid={`call-transcript-${call.id}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <FileText className="h-3.5 w-3.5 text-teal-500" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Transcript</span>
                </div>
                <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line max-h-60 overflow-y-auto bg-muted/30 rounded-md p-3">
                  {transcript}
                </div>
              </div>
            )}

            {/* Auto-scheduled meeting */}
            {hasMeeting && (
              <div className="px-4 py-3 bg-teal-50/50 dark:bg-teal-950/10" data-testid={`call-meeting-${call.id}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-teal-500" />
                  <span className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wide">Auto-Scheduled Meeting</span>
                </div>
                <p className="text-sm text-foreground">
                  {call.scheduled_meeting.meeting_type_name || call.scheduled_meeting.meeting_type || 'Demo'} &mdash;{' '}
                  {call.scheduled_meeting.start_time && formatLocal(call.scheduled_meeting.start_time, "MMMM dd, yyyy 'at' h:mm a")}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {call.scheduled_meeting.calendar_event_id && (
                    <Badge variant="outline" className="text-[10px]">
                      <Calendar className="h-3 w-3 mr-1" /> Calendar
                    </Badge>
                  )}
                  {call.scheduled_meeting.confirmation_email_sent && (
                    <Badge variant="outline" className="text-[10px]">
                      <Mail className="h-3 w-3 mr-1" /> Email Sent
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Footer meta */}
            <div className="px-4 py-2 bg-muted/20">
              <p className="text-xs text-muted-foreground">
                {isAI ? 'AI Agent' : (getCreatorName ? getCreatorName(call) : 'User')}
                {callDate ? ` \u2022 ${formatLocal(callDate)}` : ''}
                {call.to_number ? ` \u2022 ${call.to_number}` : ''}
              </p>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export const CallLogCard = React.memo(CallLogCardImpl);
